'use client';

import React from 'react';
import { styled } from '@mui/material/styles';

import {
  GlobalPreloader,
  Loading,
  BgContainer,
  SectionWithTitle,
  OverallRatingWidget,
} from 'components';
import { MarketRatingsPageData, getMarketRatingsPageData } from 'api/selectors/pages';
import { useServerData } from 'utils/react';
import {
  MarketBadDebtTranscription,
  MarketManipulationComplexityTranscription,
  MarketRatingTranscription,
} from 'components/transcriptions';
import { ProductSlug, VersionSlug, NetworkSlug } from 'domain/types';

import { HealthWidget, ImpactCostsTable, ProgressBarChart } from './components';

type MarketRatingsProps = {
  data: string;
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
  marketId: string;
};

export function MarketRatings(props: MarketRatingsProps) {
  const { data, productSlug, versionSlug, network, marketId } = props;
  const pageDataRD = useServerData(
    () => getMarketRatingsPageData({ productSlug, versionSlug, network, marketId }),
    [marketId, network, productSlug, versionSlug],
    data,
  );

  return (
    <Loading data={pageDataRD} loader={<GlobalPreloader />}>
      {pageData => <MarketRatingsContent {...pageData} />}
    </Loading>
  );
}

function MarketRatingsContent({
  badDebtInfo,
  manipulationComplexityInfo,
  rating,
  costsTableData,
}: MarketRatingsPageData) {
  return (
    <Root>
      <OverallRatingContainer>
        <OverallRatingWidget
          rating={rating}
          description={
            <MarketRatingTranscription
              badDebtInfo={badDebtInfo}
              manipulationComplexityInfo={manipulationComplexityInfo}
              marketRating={rating}
            />
          }
        />
      </OverallRatingContainer>
      {(badDebtInfo || manipulationComplexityInfo) && (
        <SectionWithTitle title="Rating components">
          {manipulationComplexityInfo && (
            <BgContainer>
              <HealthWidget
                title="Manipulation complexity"
                description={
                  <MarketManipulationComplexityTranscription {...manipulationComplexityInfo} />
                }
                rating={manipulationComplexityInfo.rating.rating}
              >
                {manipulationComplexityInfo.valueAtRisk &&
                  manipulationComplexityInfo.impactCost && (
                    <ProgressBarChart
                      rating={manipulationComplexityInfo.rating.rating}
                      firstAmount={{
                        title: 'Value at risk',
                        amount: manipulationComplexityInfo.valueAtRisk,
                      }}
                      secondAmount={{
                        title: 'Impact Cost',
                        amount: manipulationComplexityInfo.impactCost,
                      }}
                    />
                  )}
              </HealthWidget>
            </BgContainer>
          )}

          {badDebtInfo && (
            <BgContainer>
              <HealthWidget
                title="Bad Debt"
                description={<MarketBadDebtTranscription {...badDebtInfo} />}
                rating={badDebtInfo.rating.rating}
              >
                <ProgressBarChart
                  rating={badDebtInfo.rating.rating}
                  firstAmount={{
                    title: 'TVL',
                    amount: badDebtInfo.rating.payload.marketTvl,
                  }}
                  secondAmount={{
                    title: 'Bad Debts',
                    amount: badDebtInfo.rating.payload.badDebtsSum,
                  }}
                />
              </HealthWidget>
            </BgContainer>
          )}
        </SectionWithTitle>
      )}
      {!!costsTableData.length && (
        <SectionWithTitle title="Manipulation complexity">
          <ImpactCostsTable data={costsTableData} />
        </SectionWithTitle>
      )}
    </Root>
  );
}

const Root = styled('div')({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 66,
});

const OverallRatingContainer = styled('div')({
  minHeight: 336,
  height: 1,
});
